import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import {
    IconCameraOutline,
    IconExcMarkCircle,
    IconImageOutline,
    IconLockFill
} from 'assets/svg/sprite'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { Modal } from 'components-ref/molecules/modal/modal'
import { deleteMassMessage } from 'services/endpoints/message'
import { type IMassMessage } from 'types/interfaces/IMessage'

import style from './_mass-message-card.module.scss'

const MassMessageCard = ({ message }: { message: IMassMessage }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [showUnsendConfirm, setShowUnsendConfirm] = useState(false)

    const date = new Date(message.sent_date)

    const monthArray = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ]

    const month = monthArray[date.getMonth()]
    const year = date.getFullYear()
    const day = (`0${date.getDate()}`).slice(-2)

    const time = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    })

    const deleteMutation = useMutation(
        async () => await deleteMassMessage(message.id),
        {
            onSuccess: () => {
                setShowUnsendConfirm(false)
                queryClient.invalidateQueries('mass-messages')
            }
        }
    )

    const renderCategories = () => {
        if (!message.categories || message.categories.length === 0) {
            return t('None')
        }

        const names = message.categories.map(category => category.name)

        if (names.length <= 3) {
            return names.join(', ')
        }

        return `${names.slice(0, 3).join(', ')}...`
    }

    const previewUrl =
        message.preview_photo?.url ??
        message.preview_video?.thumb

    /*
     * Do not show Unsend once every originally sent message
     * has been viewed.
     */
    const canUnsend =
        !message.unsent_at &&
        message.viewed !== message.sent

    return (
        <>
            <div className={style.container}>
                <div className={style.top_container}>
                    <div className={style.media_container}>
                        <div className={style.media_wrapper}>
                            {previewUrl && (
                                <img
                                    src={previewUrl}
                                    alt='message media'
                                />
                            )}

                            {message.message_price > 0 && (
                                <div className={style.price_container}>
                                    <div className={style.price_background} />

                                    <IconLockFill />

                                    ${message.message_price}
                                </div>
                            )}
                        </div>

                        <div className={style.media_count_container}>
                            {message.video_count > 0 && (
                                <div className={style.media_count}>
                                    <IconCameraOutline
                                        color='#828C94'
                                        width='16'
                                        height='12'
                                    />

                                    {message.video_count}
                                </div>
                            )}

                            {message.photo_count > 0 && (
                                <div className={style.media_count}>
                                    <IconImageOutline
                                        color='#828C94'
                                        width='12'
                                        height='12'
                                    />

                                    {message.photo_count}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={style.stats_container}>
                        {message.message_price > 0 && (
                            <div className={style.stat}>
                                <div>{t('purchased')}</div>

                                <div className={style.stat_value}>
                                    <div
                                        className={
                                            style.stat_value_count_container
                                        }
                                    >
                                        <div
                                            className={
                                                style.stat_value_count
                                            }
                                        >
                                            {message.purchased_count}
                                        </div>
                                    </div>

                                    ${message.message_price}
                                </div>
                            </div>
                        )}

                        <div className={style.stat}>
                            <div>{t('sent')}</div>

                            <div className={style.stat_value}>
                                {message.actual_sent}
                            </div>
                        </div>

                        <div className={style.stat}>
                            <div>{t('viewed')}</div>

                            <div className={style.stat_value}>
                                {message.viewed}
                            </div>
                        </div>

                        <div className={style.stat}>
                            <div>{t('category')}</div>

                            <div
                                className={`${style.stat_value} ${style.stat_value_none}`}
                            >
                                {renderCategories()}
                            </div>
                        </div>
                    </div>
                </div>

                {message.body && (
                    <div className={style.text_container}>
                        {message.body}
                    </div>
                )}

                <div className={style.bottom_container}>
                    <div className={style.bottom_date}>
                        {t('Sent')}{' '}
                        <span>
                            {month} {day}, {year} {time}
                        </span>
                    </div>

                    {message.unsent_at ? (
                        <div className={style.unsent_text}>
                            {t('Unsent')}
                        </div>
                    ) : canUnsend ? (
                        <button
                            type='button'
                            className={style.unsend_btn}
                            onClick={() => setShowUnsendConfirm(true)}
                            disabled={deleteMutation.isLoading}
                        >
                            {t('Unsend')}

                            <IconExcMarkCircle
                                color='#828C94'
                                width='16'
                                height='16'
                            />
                        </button>
                    ) : null}
                </div>
            </div>

            {showUnsendConfirm && (
                <Modal
                    onClose={() => setShowUnsendConfirm(false)}
                    closeOnLayerClick={!deleteMutation.isLoading}
                    maxWidth={40}
                    isLayerTransparent={false}
                    title={'Unsend Mass Message'}
                    paddingBottom={0}
                    hideCloseIcon={true}
                >
                    <ConfirmModal
                        confirmButtonWidth='14'
                        body={
                            <>
                                Will unsend to any recipients
                                who have yet to view the message.
                                <br />
                                <br />
                                Do you wish to continue?
                            </>
                        }
                        confirmText={t('Unsend')}
                        confirmFn={() => deleteMutation.mutate()}
                        onCancel={() => setShowUnsendConfirm(false)}
                    />
                </Modal>
            )}
        </>
    )
}

export default MassMessageCard
